'use client';

import { useState, useCallback } from 'react';
import type { Expression } from '@capture/shared';
import { FormulaEditor } from './FormulaEditor';
import { ComputeVisualEditor } from '../compute/ComputeVisualEditor';
import { FormulaExpandModal } from './FormulaExpandModal';
import { FormulaTestPanel } from './FormulaTestPanel';

export interface FormulaFieldProps {
  value: Expression;
  onChange: (next: Expression) => void;
  fieldNames: string[];
  /**
   * Intentionally not consumed yet — reserved for future context-sensitive rendering
   * (e.g. filtering template options or adjusting validation hints per call-site).
   * Present at every call-site now so refactors can read provenance without grepping callers.
   */
  context?: 'compute' | 'rule' | 'substitute' | 'routing';
  scope?: 'record' | 'batch';
  readOnly?: boolean;
  /**
   * Accessible name for the composite widget. When provided, the root container
   * becomes role="group" with this aria-label so the field's name is announced
   * once at the group level — inner buttons keep their own names ("Visual"/"Code").
   * Callers must NOT wrap FormulaField in a <label> (that pollutes every inner
   * control's accessible name). When omitted, the root renders unchanged (no role).
   */
  ariaLabel?: string;
}

type Mode = 'visual' | 'code';

export function FormulaField({
  value,
  onChange,
  fieldNames,
  scope,
  readOnly = false,
  ariaLabel,
}: FormulaFieldProps) {
  const [mode, setMode] = useState<Mode>('visual');
  const [expanded, setExpanded] = useState(false);

  const handleSource = useCallback(
    (source: string) => onChange({ source }),
    [onChange],
  );

  return (
    <div
      className="formula-field"
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      <div className="formula-field__mode-toggle">
        <button
          type="button"
          className={mode === 'visual' ? 'is-active' : ''}
          onClick={() => setMode('visual')}
        >
          Visual
        </button>
        <button
          type="button"
          className={mode === 'code' ? 'is-active' : ''}
          onClick={() => setMode('code')}
        >
          Code
        </button>
        {mode === 'code' && !readOnly && (
          <button
            type="button"
            className="formula-field__expand"
            onClick={() => setExpanded(true)}
          >
            Expand
          </button>
        )}
      </div>

      {readOnly ? (
        <FormulaEditor
          value={value.source}
          onChange={handleSource}
          fieldNames={fieldNames}
          scope={scope}
          readOnly
        />
      ) : mode === 'visual' ? (
        <ComputeVisualEditor
          formula={value.source}
          onChange={handleSource}
          fieldNames={fieldNames}
          scope={scope}
        />
      ) : (
        <FormulaEditor
          value={value.source}
          onChange={handleSource}
          fieldNames={fieldNames}
          scope={scope}
          readOnly={readOnly}
        />
      )}

      {mode === 'code' && !readOnly && (
        <details className="formula-field__test">
          <summary>Test this formula</summary>
          <FormulaTestPanel formula={value.source} />
        </details>
      )}

      {mode === 'code' && expanded && (
        <FormulaExpandModal
          value={value.source}
          onChange={handleSource}
          fieldNames={fieldNames}
          scope={scope}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}
